
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertCircle, Save } from 'lucide-react';
import { useState } from 'react';

interface PEOProviderProfileProps {
  providerInfo: any;
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
  form: {
    name: string;
    contact_email: string;
    years_in_business: string;
    employee_range: string;
  };
  setForm: React.Dispatch<React.SetStateAction<any>>;
  onSave: (e: React.FormEvent) => void;
  saving: boolean;
}

export const PEOProviderProfile = ({
  providerInfo,
  isEditing,
  setIsEditing,
  form,
  setForm,
  onSave,
  saving,
}: PEOProviderProfileProps) => {
  const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev: any) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  if (!providerInfo) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Your PEO Provider Profile</CardTitle>
        </CardHeader>
        <CardContent>
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>No Provider Profile</AlertTitle>
            <AlertDescription>
              Your provider profile is not set up yet. Please contact support if you believe this is an error.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Your PEO Provider Profile</CardTitle>
      </CardHeader>
      <CardContent>
        {!isEditing ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Provider Name</p>
                <p className="text-lg font-semibold">{providerInfo.name || <span className="text-gray-400 italic">Not set</span>}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Contact Email</p>
                <p className="text-lg">{providerInfo.contact_email || <span className="text-gray-400 italic">Not set</span>}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Years in Business</p>
                <p className="text-lg">{providerInfo.years_in_business !== null && providerInfo.years_in_business !== undefined ? providerInfo.years_in_business : <span className="text-gray-400 italic">Not set</span>}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Employee Range</p>
                <p className="text-lg">{providerInfo.employee_range || <span className="text-gray-400 italic">Not set</span>}</p>
              </div>
            </div>
            <div className="flex justify-end">
              <Button variant="default" onClick={() => setIsEditing(true)}>
                Edit Information
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={onSave} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-muted-foreground">Provider Name</label>
                <input
                  id="name"
                  name="name"
                  value={form.name}
                  onChange={handleFieldChange}
                  className="w-full border-gray-300 rounded px-3 py-2 mt-1"
                  required
                  placeholder="Enter your provider's legal name"
                  disabled={saving}
                />
              </div>
              <div>
                <label htmlFor="contact_email" className="block text-sm font-medium text-muted-foreground">Contact Email</label>
                <input
                  id="contact_email"
                  name="contact_email"
                  type="email"
                  value={form.contact_email}
                  onChange={handleFieldChange}
                  className="w-full border-gray-300 rounded px-3 py-2 mt-1"
                  required
                  placeholder="Contact email"
                  disabled={saving}
                />
              </div>
              <div>
                <label htmlFor="years_in_business" className="block text-sm font-medium text-muted-foreground">Years in Business</label>
                <input
                  id="years_in_business"
                  name="years_in_business"
                  type="number"
                  min={0}
                  value={form.years_in_business}
                  onChange={handleFieldChange}
                  className="w-full border-gray-300 rounded px-3 py-2 mt-1"
                  required
                  placeholder="Years"
                  disabled={saving}
                />
              </div>
              <div>
                <label htmlFor="employee_range" className="block text-sm font-medium text-muted-foreground">Employee Range</label>
                <input
                  id="employee_range"
                  name="employee_range"
                  value={form.employee_range}
                  onChange={handleFieldChange}
                  className="w-full border-gray-300 rounded px-3 py-2 mt-1"
                  required
                  placeholder="e.g. 1-99"
                  disabled={saving}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="secondary" onClick={() => setIsEditing(false)} disabled={saving}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="default"
                className="flex items-center gap-2"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Save className="h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
};

